import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "../../axiosConfig";
import { TabLayout } from "../../types/ui-types";
import { ItemType } from "../../types";

// Dictionary type of tabs by listing ID
export type TabLayoutDict = {
  [listingId: string]: TabLayout[];
};

type ItemTypeDetails = {
  id: ItemType;
  label: string;
  defaultIcon?: string;
};

interface UiState {
  tabLayouts: TabLayoutDict | null;
  itemTypes: ItemTypeDetails[];
}

const initialState: UiState = {
  tabLayouts: null,
  itemTypes: [],
};

// Async thunk for fetching listings (filtered by ui)
export const fetchTabLayouts = createAsyncThunk(
  "ui/fetchTabLayouts",
  async () => {
    const response = await axios.get("/api/tab-layouts");
    const ret: TabLayout[] = response.data;
    return ret;
  }
);

// Fetch item types
export const fetchItemTypes = createAsyncThunk(
  "ui/fetchItemTypes",
  async () => {
    const response = await axios.get("/api/item-types");
    const ret: ItemTypeDetails[] = response.data;
    return ret;
  }
);

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchTabLayouts.fulfilled,
        (state, action: PayloadAction<TabLayout[]>) => {
          // Transform array into dictionary by pageListingId
          const tabLayoutDict: TabLayoutDict = {};
          action.payload.forEach((tabLayout) => {
            const { pageListingId } = tabLayout;
            if (!tabLayoutDict[pageListingId]) {
              tabLayoutDict[pageListingId] = [];
            }
            tabLayoutDict[pageListingId].push(tabLayout);
          });
          state.tabLayouts = tabLayoutDict;
        }
      )
      .addCase(
        fetchItemTypes.fulfilled,
        (state, action: PayloadAction<ItemTypeDetails[]>) => {
          state.itemTypes = action.payload;
        }
      );
  },
});

// Select tab layouts for a given listing ID
export const selectTabLayoutForListing = (
  state: { ui: UiState },
  listingId: string
): TabLayoutDict => {
  const itemsForListing = state.ui.tabLayouts?.[listingId] || [];

  const reduced = itemsForListing.reduce((acc, curr) => {
    const itemListingId = curr.itemListingId;
    if (acc[itemListingId]) {
      return {
        ...acc,
        [itemListingId]: [...acc[itemListingId], curr],
      };
    } else {
      return {
        ...acc,
        [itemListingId]: [curr],
      };
    }
  }, {} as TabLayoutDict);

  return reduced;
};

export default uiSlice.reducer;
